import React, { useMemo } from 'react';
import { INNER_CIRCLE_TEST_ID } from 'Shared/components/design/promoStepper/components/constants';
import {
  ActivitiesContainer,
  ActivitiesProgress,
  ActivityState,
  ActivityStateWrapper,
  InnerCircle,
  Line,
  StepNumber
} from 'Shared/components/design/promoStepper/components/style';
import { ICON_IDS } from 'Shared/components/icons/constants';
import Icon from 'Shared/components/icons/icon';
import { PROMOStep } from 'Shared/models/swagger/promo';
import { STEP_TYPE } from 'Shared/services/promo/app/promoService';

interface ActivityProgressProps {
  steps: PROMOStep[];
  state: number;
  daysRequired?: number;
  optinTs: string | undefined | null;
  currentDay: number;
}

interface PROMOStepWithDesignProps extends PROMOStep {
  hasInnerCircle?: boolean;
  hasProgress?: boolean;
}

/**
 * Determines which steps should have an inner circle based on the completion status.
 */
function determineCurrentPendingComplete(steps: PROMOStep[]): PROMOStepWithDesignProps[] {
  return steps.map((step, index) => {
    const isFirstStep = index === 0;
    const isLastStep  = index === steps.length - 1;

    const hasInnerCircle =
      // inner circle for first step if it is not completed
      (isFirstStep && step.completedAt == null) ||
      // inner circle for step following a completed step
      (index > 0 && steps[index - 1].completedAt != null) ||
      // inner circle for last step if it has a completed ts
      (isLastStep && step.completedAt != null);

    return {
      ...step,
      hasInnerCircle,
    };
  });
}

/**
 * Determines which steps should have an inner circle based on the completion status.
 */
function determineStepsWithDaysRequired(steps: PROMOStep[], daysRequired: number | undefined, optinTs: string | undefined | null, currentDay: number): PROMOStepWithDesignProps[] {
  if (!daysRequired || !optinTs) {
    return steps;
  }

  const completedSteps = steps.reduce((acc, step) => {
    if (acc.length === daysRequired) {
      return acc;
    }

    if (step.completedAt) {
      acc.push({
        ...step,
        value: (acc.length === 0 ? 'Day ' : '') + (acc.length + 1).toString(),
      });
    }

    return acc;
  }, [] as PROMOStepWithDesignProps[]);

  // We've already filled all the days lets render
  if (completedSteps.length === daysRequired) {
    return completedSteps;
  }

  // Get which day we are currently on in the promo
  const currentDayInPromo = (currentDay === 0) ? 0 : currentDay - 1;

  // If the current day has already been completed
  // then let's exclude it so that it can't have the
  // inner circle.
  if (currentDayInPromo < steps.length && !steps[currentDayInPromo].completedAt) {
    completedSteps.push({
      ...steps[currentDayInPromo],
      value: (completedSteps.length + 1).toString(),
      hasInnerCircle: true,
      hasProgress: true,
    });
  }

  // Add the remaining steps that are not completed
  for (let x = completedSteps.length; x < daysRequired; x++) {
    completedSteps.push({
      ...steps[x],
      value: (x + 1).toString(),
      hasInnerCircle: false,
      hasProgress: false,
      completedAt: null,
    });
  }

  return completedSteps;
}

function getCompletedAtIcon(isCompleted: boolean): JSX.Element {
  if (isCompleted) {
    return <Icon iconId={ICON_IDS.CHECKMARK} height={18} width={18} color="white" />;
  }

  return <></>;
}

function getInnerCircle(step: PROMOStepWithDesignProps): JSX.Element {
  if (step.hasProgress) {
    let progress = 0;

    if (step.target) {
      progress = ((step.activityCounter || 0) / step.target) * 100;
    }

    return <InnerCircle data-testid={INNER_CIRCLE_TEST_ID} progress={progress} />;
  } else if (step.hasInnerCircle) {
    return <InnerCircle data-testid={INNER_CIRCLE_TEST_ID} progress={0} />;
  }

  return <></>;
}

function getConnectingLine(isLastStep: boolean): JSX.Element {
  if (isLastStep) {
    return <></>;
  }

  return <Line />;
}

/**
 * Component that renders the progress of the activities
 * @param param0
 * @returns
 */
function ActivityProgress({ steps, state, daysRequired, optinTs, currentDay }: ActivityProgressProps): JSX.Element {
  const updatedSteps: PROMOStepWithDesignProps[] = useMemo(() => {
    if (state === STEP_TYPE.ACTIVITIES_PROGRESS) {
      return determineCurrentPendingComplete(steps);
    }

    if (state === STEP_TYPE.ACTIVITIES_PROGRESS_DAYS_REQUIRED) {
      return determineStepsWithDaysRequired(steps, daysRequired, optinTs, currentDay);
    }

    return steps;
  }, [steps, state, daysRequired, optinTs, currentDay]);

  return (
    <ActivitiesContainer>
      <ActivitiesProgress>
        {updatedSteps.map((step, index) => (
          <React.Fragment key={index}>
            <ActivityStateWrapper>
              <ActivityState isCompleted={!!step.completedAt}>
                {getCompletedAtIcon(!!step.completedAt)}
                {getInnerCircle(step)}
                <StepNumber>{step.value}</StepNumber>
              </ActivityState>
            </ActivityStateWrapper>
            {getConnectingLine(index === updatedSteps.length - 1)}
          </React.Fragment>
        ))}
      </ActivitiesProgress>
    </ActivitiesContainer>
  );
}

export default ActivityProgress;
